// =============================================================================
// FILE:
// frontend/src/pms/task_execution/components/TaskGrid.js
// =============================================================================

import React from "react";


// =============================================================================
// COMPONENT
// =============================================================================

export default function TaskGrid({

  rows = [],

  loading = false,

  onRowClick

}) {


  // ===========================================================================
  // LOADING
  // ===========================================================================
  if (loading) {

    return (

      <div style={styles.loader}>

        Loading tasks...

      </div>
    );
  }


  // ===========================================================================
  // EMPTY
  // ===========================================================================
  if (!rows || rows.length === 0) {

    return (

      <div style={styles.emptyState}>

        No tasks found

      </div>
    );
  }


  // ===========================================================================
  // MAIN RENDER
  // ===========================================================================
  return (

    <div style={styles.container}>


      {/* ================================================================ */}
      {/* HEADER */}
      {/* ================================================================ */}
      <div style={styles.headerRow}>


        <div style={styles.colId}>
          ID
        </div>


        <div style={styles.colTask}>
          Task
        </div>


        <div style={styles.colPhase}>
          Phase
        </div>


        <div style={styles.colReadiness}>
          Readiness
        </div>


        <div style={styles.colStatus}>
          Status
        </div>


        <div style={styles.colPriority}>
          Priority
        </div>


        <div style={styles.colRequests}>
          Requests
        </div>


        <div style={styles.colExecution}>
          Execution
        </div>

      </div>


      {/* ================================================================ */}
      {/* BODY */}
      {/* ================================================================ */}
      <div style={styles.body}>


        {

          rows.map((row) => (

            <div

              key={row.id}

              style={styles.dataRow}

              onClick={() =>
                onRowClick(row)
              }
            >


              {/* -------------------------------------------------------- */}
              {/* ID */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colId}>


                <div style={styles.primaryText}>

                  {row.id}

                </div>

              </div>


              {/* -------------------------------------------------------- */}
              {/* TASK */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colTask}>


                <div style={styles.primaryText}>

                  {row.task_name}

                </div>


                <div style={styles.secondaryText}>

                  {
                    row.project_name ||

                    "Receiver Vessel Project"
                  }

                </div>

              </div>


              {/* -------------------------------------------------------- */}
              {/* PHASE */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colPhase}>


                <div style={styles.primaryText}>

                  {row.phase_name}

                </div>

              </div>


              {/* -------------------------------------------------------- */}
              {/* READINESS */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colReadiness}>


                <div style={styles.readinessWrap}>


                  <div style={styles.progressBar}>


                    <div
                      style={{
                        ...styles.progressFill,

                        width:
                          `${
                            row.readiness_percent || 0
                          }%`,

                        background:
                          getReadinessColor(
                            row.readiness_percent || 0
                          )
                      }}
                    />

                  </div>


                  <div style={styles.readinessText}>

                    {
                      row.readiness_percent || 0
                    }%

                  </div>

                </div>

              </div>


              {/* -------------------------------------------------------- */}
              {/* STATUS */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colStatus}>


                <span style={
                  getStatusStyle(
                    row.readiness_status
                  )
                }>

                  {
                    row.readiness_status ||

                    "WAITING"
                  }

                </span>

              </div>


              {/* -------------------------------------------------------- */}
              {/* PRIORITY */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colPriority}>


                <span style={
                  getPriorityStyle(
                    row.priority
                  )
                }>

                  {
                    row.priority ||

                    "NORMAL"
                  }

                </span>

              </div>


              {/* -------------------------------------------------------- */}
              {/* REQUESTS */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colRequests}>


                <div style={styles.primaryText}>

                  {
                    row.pending_requests || 0
                  }

                </div>

              </div>


              {/* -------------------------------------------------------- */}
              {/* EXECUTION */}
              {/* -------------------------------------------------------- */}
              <div style={styles.colExecution}>


                <span style={
                  getExecutionStyle(
                    row.execution_status
                  )
                }>

                  {
                    formatExecutionStatus(
                      row.execution_status
                    )
                  }

                </span>

              </div>

            </div>
          ))
        }

      </div>

    </div>
  );
}


// =============================================================================
// HELPERS
// =============================================================================

function getReadinessColor(percent) {

  if (percent >= 80) {
    return "#16a34a";
  }

  if (percent >= 40) {
    return "#ea580c";
  }

  return "#dc2626";
}


function getStatusStyle(status) {

  switch (status) {

    case "READY":

      return styles.statusGreen;

    case "ACTIVE":

      return styles.statusBlue;

    case "BLOCKED":

      return styles.statusRed;

    case "WAITING":

      return styles.statusOrange;

    default:

      return styles.statusGray;
  }
}


function getPriorityStyle(priority) {

  switch (priority) {

    case "CRITICAL":

      return styles.priorityCritical;

    case "HIGH":

      return styles.priorityHigh;

    case "NORMAL":

      return styles.priorityNormal;

    case "LOW":

      return styles.priorityLow;

    default:

      return styles.priorityNormal;
  }
}


function getExecutionStyle(status) {

  switch (status) {

    case "IN_PROGRESS":

      return styles.executionBlue;

    case "READY":

      return styles.executionGreen;

    case "WAITING_MATERIAL":

      return styles.executionOrange;

    default:

      return styles.executionGray;
  }
}


function formatExecutionStatus(status) {

  if (!status) {
    return "Not Started";
  }

  return status
    .replaceAll("_", " ")
    .toUpperCase();
}


// =============================================================================
// STYLES
// =============================================================================

const styles = {

  container: {

    background: "#ffffff",

    borderRadius: 14,

    overflow: "hidden",

    boxShadow:
      "0 1px 4px rgba(0,0,0,0.08)"
  },

  headerRow: {

    display: "flex",

    background: "#f3f4f6",

    borderBottom:
      "1px solid #e5e7eb",

    padding: "14px 16px",

    fontSize: 13,

    fontWeight: 700,

    color: "#374151"
  },

  body: {

    display: "flex",

    flexDirection: "column"
  },

  dataRow: {

    display: "flex",

    padding: "16px",

    borderBottom:
      "1px solid #f3f4f6",

    alignItems: "center",

    cursor: "pointer",

    transition:
      "background 0.2s ease"
  },

  primaryText: {

    fontWeight: 600,

    fontSize: 14,

    color: "#111827"
  },

  secondaryText: {

    fontSize: 12,

    color: "#6b7280",

    marginTop: 4
  },

  readinessWrap: {

    display: "flex",

    alignItems: "center",

    gap: 10
  },

  progressBar: {

    width: 120,

    height: 10,

    background: "#e5e7eb",

    borderRadius: 999,

    overflow: "hidden"
  },

  progressFill: {

    height: "100%",

    borderRadius: 999
  },

  readinessText: {

    fontSize: 12,

    fontWeight: 700,

    color: "#374151"
  },

  colId: {

    width: 80
  },

  colTask: {

    width: 260,

    paddingRight: 12
  },

  colPhase: {

    width: 180
  },

  colReadiness: {

    width: 220
  },

  colStatus: {

    width: 140,

    display: "flex",

    justifyContent: "center"
  },

  colPriority: {

    width: 140,

    display: "flex",

    justifyContent: "center"
  },

  colRequests: {

    width: 120,

    display: "flex",

    justifyContent: "center"
  },

  colExecution: {

    flex: 1,

    display: "flex",

    justifyContent: "center"
  },

  statusGreen: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusRed: {

    background: "#fee2e2",

    color: "#991b1b",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  statusGray: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  priorityCritical: {

    background: "#991b1b",

    color: "#ffffff",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  priorityHigh: {

    background: "#dc2626",

    color: "#ffffff",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  priorityNormal: {

    background: "#2563eb",

    color: "#ffffff",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  priorityLow: {

    background: "#6b7280",

    color: "#ffffff",

    padding: "6px 10px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  executionGreen: {

    background: "#dcfce7",

    color: "#166534",

    padding: "6px 12px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  executionBlue: {

    background: "#dbeafe",

    color: "#1d4ed8",

    padding: "6px 12px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  executionOrange: {

    background: "#ffedd5",

    color: "#c2410c",

    padding: "6px 12px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  executionGray: {

    background: "#e5e7eb",

    color: "#374151",

    padding: "6px 12px",

    borderRadius: 999,

    fontSize: 12,

    fontWeight: 700
  },

  loader: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center"
  },

  emptyState: {

    background: "#ffffff",

    borderRadius: 14,

    padding: 40,

    textAlign: "center",

    color: "#6b7280"
  }
};